import {ButtonWithIcon} from 'components/ButtonWithIcon';
import {Exist} from 'components/Exist';
import {HOCModal} from 'components/HOCModal';
import {HOCModalCenterDesktopPosition} from 'components/HOCModalCenterDesktopPosition';
import {setWeeklyPrizeViewState} from 'modules/actions/user';
import {RequestState} from 'modules/enums';
import {
	getUserStore,
	isSecondTutorialViewed,
	loadingUserState,
	isWeeklyPrizeViewed,
	getWeeklyPrizeModalData,
} from 'modules/selectors';
import {useAdobeDTM, useIsMobile} from 'modules/utils/hooks';
import React, {Fragment, useCallback, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import styled from '@emotion/styled';
import DOMPurify from 'dompurify';
import RallySSO from 'modules/utils/SSO/rallySSO';
import {getLogoAccordingState} from 'modules/utils/dom_helpers';
import {redirectForSponsor} from 'modules/utils/mlbConfig';
import {fetchWeeklyPrizeModal} from 'modules/actions/weeklyPrizeModal';
import {IMAGES_URL} from 'modules';

const Wrapper = styled.div`
	overflow-y: auto;
	overflow-x: hidden;
	height: 100%;
`;

const Controls = styled.div`
	> button {
		margin: 0 auto 30px auto;
		display: block;
		background: transparent;
		border: none;

		img {
			max-width: 125px;
		}
	}
`;

const Title = styled.p`
	font-weight: 800;
	font-size: 30px;
	line-height: 28px;
	text-align: center;
	padding: 0 16px;
`;

const ImageWrapper = styled.div`
	padding: 16px;
	img {
		width: 100%;
		height: auto;
		object-fit: contain;
		max-height: 150px;
	}
`;

const Description = styled.div`
	text-align: left;
	color: #ffff;
	font-size: 14px;
	max-width: 90%;
	width: 100%;
	padding: 16px;
	margin: 0 auto;

	ul {
		padding: 0 20px;

		li {
			list-style: disc;
			line-height: 1.8;
		}

		a {
			color: #2687ff;
			font-weight: bold;
		}
	}
`;

const Buttons = styled.div`
	display: flex;
	position: relative;
	bottom: 20px;
	padding-top: 40px;
`;

const NextButton = styled.button`
	background: #2687ff;
	color: #ffffff;
`;

export const WeeklyPrizeModal: React.FC = () => {
	const dispatch = useDispatch();
	const modalData = useSelector(getWeeklyPrizeModalData);

	const isViewed = useSelector(isWeeklyPrizeViewed);
	const isSecondViewed = useSelector(isSecondTutorialViewed);
	const loadState = useSelector(loadingUserState);
	const i18n = useTranslation();
	const isMobile = useIsMobile();
	const {countryCode, stateCode} = useSelector(getUserStore);
	const isOpen = [!isViewed, isSecondViewed].every(Boolean);
	const isHidden = [!isOpen, loadState !== RequestState.RECEIVED, !modalData].some(Boolean);
	const {trackState, trackAction} = useAdobeDTM();


	const prepareLink = (event: React.SyntheticEvent<HTMLDivElement>) => {
		event.stopPropagation();
		event.preventDefault();

		const target = event.target as HTMLAnchorElement;
		const href = target.getAttribute("href");
		if (target.tagName.toLowerCase() === "a" && href) {
			if (RallySSO.IS_NATIVE) {
				RallySSO.nativeBridge?.launchExternal(href);
			} else {
				window.open(href, "_blank");
			}
		}
	};

	useEffect(() => {
		dispatch(fetchWeeklyPrizeModal())
	}, [dispatch]);

	useEffect(() => {
		if (!isHidden) {
			trackState({
				"page.name": "mlb play : beat the streak : weekly prize",
				"page.subType": "weekly prize",
			});
		}
	}, [isHidden, trackState]);

	const handleClick = useCallback(() => {
		redirectForSponsor(countryCode);
	}, [countryCode]);

	const onPlay = () => {
		trackAction({
			"page.name": "mlb play : beat the streak : weekly prize",
			"action.name": "mlb play : mlb play : beat the streak : weekly prize : play click",
			"action.element.text": "play",
		});
		dispatch(setWeeklyPrizeViewState());
	};

	if (isHidden || !modalData) {
		return null;
	}

	const Content = () => (
		<Wrapper>
			<Controls>
				<button onClick={handleClick}>
					<img
						src={getLogoAccordingState(countryCode, stateCode)}
						alt="The main logo of MLB"
					/>
				</button>

				<Title>{modalData.title}</Title>

				<ImageWrapper>
					<img src={`${IMAGES_URL}image/${modalData.image}`} alt={modalData.title}/>
				</ImageWrapper>
				<Description onClick={prepareLink}>
					<ul
						// eslint-disable-next-line react/no-danger
						dangerouslySetInnerHTML={{
							__html: DOMPurify.sanitize(modalData.description),
						}}
					/>
				</Description>

				<Buttons>
					<ButtonWithIcon
						text={i18n.t("weekly_prize.button.play", "Done")}
						styleExtend={NextButton}
						onClick={onPlay}
					/>
				</Buttons>
			</Controls>
		</Wrapper>
	);

	const props = {
		isOpen: true,
		onClose: onPlay,
		name: "weekly-prize-modal",
		headerText: <Fragment>{modalData.header}</Fragment>,
	};

	return (
		<Fragment>
			<Exist when={isMobile}>
				<HOCModal {...props}>
					<Content />
				</HOCModal>
			</Exist>
			<Exist when={!isMobile}>
				<HOCModalCenterDesktopPosition {...props}>
					<Content />
				</HOCModalCenterDesktopPosition>
			</Exist>
		</Fragment>
	);
};
